import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, Paper, CircularProgress, Grid, Button, Alert,
    TextField, Snackbar, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import ImageUploader from '../../components/ImageUploader';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';

const CurrencyEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [identifier, setIdentifier] = useState('');
    const [name, setName] = useState('');
    const [iconUrl, setIconUrl] = useState('');
    const [sortOrder, setSortOrder] = useState(0);

    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            setLoading(true);
            apiService.get(`/api/currencies/${id}`)
                .then(data => {
                    setIdentifier(data.identifier || '');
                    setName(data.name || '');
                    setIconUrl(data.iconUrl || '');
                    setSortOrder(data.sortOrder ?? 0);
                })
                .catch(err => setError(err.message || 'Failed to load currency data'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError(null);

        const data = {
            id: isNew ? null : parseInt(id),
            identifier, name, iconUrl,
            sortOrder: sortOrder ? parseInt(sortOrder) : 0,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/currencies', data)
                : apiService.put(`/api/currencies/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Currency saved successfully!'});
            setTimeout(() => navigate('/currencies'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save currency');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Currency' : `Edit Currency: ${name}`}
            </Typography>

            <Protected component={CurrencyEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Configuration</Typography>
                                <Stack spacing={2}>
                                    <TextField required fullWidth label="Identifier" value={identifier}
                                               onChange={e => setIdentifier(e.target.value)}/>
                                    <TextField required fullWidth label="Name" value={name}
                                               onChange={e => setName(e.target.value)}/>
                                    <TextField fullWidth type="number" label="Sort Order" value={sortOrder}
                                               onChange={e => setSortOrder(e.target.value)}/>
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <ImageUploader
                                imageUrl={iconUrl}
                                onImageUrlChange={setIconUrl}
                                uploadPath="Currency"
                                baseFileName={name}
                                isUploadEnabled={!!name}
                            />
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={CurrencyEditPage} action={requiredAction}>
                            <Button type="submit" variant="contained"
                                    startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                    disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Currency'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/currencies" variant="outlined"
                                startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

CurrencyEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default CurrencyEditPage;
